import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import type { CoachingAdherenceRepScore } from '../../aida/constants/coachingAdherence';
import { type LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

export interface CoachingAdherenceScoreboard {
	/** Null from `adherence/me` when the rep is in no coaching cohort. */
	cohort: string | null;
	start: string;
	end: string;
	/** A call scoring at least this counts as following the coach. */
	followedScore: number;
	/** Already sorted server-side by share of past-hook calls followed. */
	reps: CoachingAdherenceRepScore[];
}

interface UseCoachingAdherenceScoreboardQueryOptions {
	cohort: string | null;
	/** ISO instants; `end` is exclusive and at most 31 days after `start`. */
	start: string | null;
	end: string | null;
}

const BASE_QUERY_KEY = 'aida/coffee/coaching/adherence/scoreboard';

export function useCoachingAdherenceScoreboardQuery({
	cohort,
	start,
	end,
}: UseCoachingAdherenceScoreboardQueryOptions) {
	const userSession = useUserSession();
	const accountId = userSession.account?.id;

	return useQuery({
		queryKey: [BASE_QUERY_KEY, accountId, cohort, start, end],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<CoachingAdherenceScoreboard>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'aida/coffee/coaching/adherence/scoreboard',
					queryParams: { cohort: cohort ?? '', start: start ?? '', end: end ?? '' },
				}),
				'GET'
			),
		enabled: Boolean(cohort && start && end),
		staleTime: 60_000,
		meta: {
			toastErrorMessage: 'Failed to load the adherence scoreboard',
			errorLogging: { category: 'AICoachingReview', action: 'loadAdherenceScoreboard' },
		} satisfies LevStandardQueryMeta,
	});
}

export function invalidateCoachingAdherenceScoreboardQueries() {
	return reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
}
